import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { OrdersService } from '../../orders.service';
import { OrderItem, OrderTypeTexts, Shipment, ShipmentStatus, Transporter } from '../../orders.models';
import { NO_SHIPMENT_DETAILS_FOUND_ALERT_MSG } from '../../../common/helpers/constants';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-shipment-details',
  standalone: false,
  templateUrl: './shipment-details.component.html',
  styleUrls: ['./shipment-details.component.scss'],
})
export class ShipmentDetailsComponent implements OnInit {
  @ViewChild('transporterModal') transporterModalRef!: TemplateRef<any>;
  @ViewChild('dispatchModal') dispatchModalRef!: TemplateRef<any>;
  @ViewChild('deliveryModal') deliveryModalRef!: TemplateRef<any>;

  shipmentId: string | null = null;
  shipment: Shipment | null = null;
  isLoading = false;

  protected transporters: Transporter[] = [];
  protected isTransportersLoading = false;
  protected isActionLoading = false;
  protected selectedTransporterId: string | null = null;
  protected expectedDeliveryDate: string = '';
  protected deliveryDate: string = '';
  protected readonly today: string = new Date().toISOString().split('T')[0];
  private modalRef: NgbModalRef | null = null;

  protected readonly ShipmentStatus = ShipmentStatus;
  protected readonly OrderTypeTexts = OrderTypeTexts;
  protected readonly NO_SHIPMENT_DETAILS_FOUND_ALERT_MSG = NO_SHIPMENT_DETAILS_FOUND_ALERT_MSG;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private ordersService: OrdersService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.shipmentId = params.get('id');
      if (this.shipmentId) {
        this.loadShipment();
      }
    });
  }

  loadShipment() {
    if (!this.shipmentId) return;
    this.isLoading = true;
    this.ordersService
      .get_shipment(this.shipmentId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.shipment = data;
        },
        error: () => {
          this.shipment = null;
        },
      });
  }

  goBack(): void {
    this.location.back();
  }

  /** The shipment endpoint has no total field, so the total is summed from its items. */
  protected get totalBoxes(): number {
    return (this.shipment?.items ?? []).reduce((total, item) => total + (item.box_quantity ?? 0), 0);
  }

  protected get canAssignTransporter(): boolean {
    return this.shipment?.status === ShipmentStatus.PENDING || this.shipment?.status === ShipmentStatus.PACKED;
  }

  protected get canMarkDispatched(): boolean {
    return this.canAssignTransporter && !!this.shipment?.transporter;
  }

  protected get canMarkDelivered(): boolean {
    return this.shipment?.status === ShipmentStatus.DISPATCHED || this.shipment?.status === ShipmentStatus.IN_TRANSIT;
  }

  // ── Assign Transporter ───────────────────────────────────────────────────

  protected openAssignTransporterModal(): void {
    this.selectedTransporterId = this.shipment?.transporter?.id || null;

    if (this.transporters.length === 0) {
      this.isTransportersLoading = true;
      this.ordersService
        .get()
        .pipe(finalize(() => (this.isTransportersLoading = false)))
        .subscribe({ next: data => (this.transporters = data.results) });
    }

    this.modalRef = this.modalService.open(this.transporterModalRef, { backdrop: 'static' });
  }

  protected confirmAssignTransporter(): void {
    if (!this.shipmentId || !this.selectedTransporterId) return;

    this.isActionLoading = true;
    this.ordersService
      .update_shipment(this.shipmentId, { transporter: this.selectedTransporterId })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this.shipment = updated;
          this.modalRef?.close();
        },
      });
  }

  // ── Mark Dispatched ──────────────────────────────────────────────────────

  protected openMarkDispatchedModal(): void {
    this.expectedDeliveryDate = '';
    this.modalRef = this.modalService.open(this.dispatchModalRef, { backdrop: 'static' });
  }

  protected confirmDispatch(): void {
    if (!this.shipmentId || !this.expectedDeliveryDate) return;

    this.isActionLoading = true;
    this.ordersService
      .update_shipment_status(this.shipmentId, {
        status: ShipmentStatus.DISPATCHED,
        delivery_date: this.expectedDeliveryDate,
      })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this.shipment = updated;
          this.modalRef?.close();
        },
      });
  }

  // ── Mark Delivered ───────────────────────────────────────────────────────

  protected openMarkDeliveredModal(): void {
    this.deliveryDate = this.today;
    this.modalRef = this.modalService.open(this.deliveryModalRef, { backdrop: 'static' });
  }

  protected confirmDelivery(): void {
    if (!this.shipmentId || !this.deliveryDate) return;

    this.isActionLoading = true;
    this.ordersService
      .update_shipment_status(this.shipmentId, {
        status: ShipmentStatus.DELIVERED,
        delivery_date: this.deliveryDate,
      })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this.shipment = updated;
          this.modalRef?.close();
        },
      });
  }

  /** "5 Kg × 4 · Carton": the packaging line shown under every product name. */
  protected getPackagingLabel(item: OrderItem): string {
    const packaging = item.product_packaging;
    const parts = [`${packaging.packing_size} ${packaging.unit?.short_name ?? ''}`.trim()];

    if (packaging.nos) {
      parts.push(`× ${packaging.nos}`);
    }
    if (packaging.packaging_type) {
      parts.push(`· ${packaging.packaging_type.name}`);
    }

    return parts.join(' ');
  }

  protected getProductTitle(item: OrderItem): string {
    const product = item.product_packaging.product;

    return typeof product === 'string' ? product : ((product as any)?.name ?? '-');
  }
}
